import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Solution.module.css';
import screenshot1 from '../../assets/images/screenshot-1.png';
import screenshot2 from '../../assets/images/screenshot-2.png';
import screenshot3 from '../../assets/images/screenshot-3.png';

const Solution = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [activeTab, setActiveTab] = useState(0);

  const solutions = [
    {
      id: 'sve',
      tag: 'SVE',
      title: 'Share Valuation Engine',
      subtitle: 'Dynamic Risk-Adjusted Valuation',
      description: 'Transparent, real-time valuation of institutional shares using hybrid ML models. Combines fundamental analysis with machine learning for accurate collateralization.',
      features: [
        'Automated NAV calculation',
        'Risk adjustment factors (LAF, RAF, MSF)',
        'Real-time market data integration',
        'Compliance & audit trails'
      ],
      screenshot: screenshot1,
      color: 'blue'
    },
    {
      id: 'cse',
      tag: 'CSE',
      title: 'Credit Scoring Engine',
      subtitle: 'AI-Powered Credit Assessment',
      description: 'Intelligent credit scoring analyzing 150+ data points with XGBoost and Random Forest algorithms. Delivers instant, explainable credit decisions with 92% accuracy.',
      features: [
        '150+ data point analysis',
        '92% prediction accuracy',
        'Explainable AI framework',
        'Real-time scoring updates'
      ],
      screenshot: screenshot2,
      color: 'pink'
    },
    {
      id: 'decisioning',
      tag: 'INSTANT',
      title: 'Instant Decisioning',
      subtitle: 'Real-Time Loan Approvals',
      description: 'End-to-end automated loan processing delivering pre-approvals in under 7 minutes. Available 24/7 with seamless API integration.',
      features: [
        'Under 7-minute approvals',
        '24/7 availability',
        'Automated workflows',
        'API-first architecture'
      ],
      screenshot: screenshot3,
      color: 'gradient'
    }
  ];

  const activeContent = solutions[activeTab];

  return (
    <section className={styles.section} id="solutions" data-section="solutions" ref={ref}>
      <div className={styles.background}>
        <div className={styles.backgroundGrid}></div>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
      </div>

      <div className={styles.container}>
        
        {/* Header */}
        <motion.div 
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        >
          <div className={styles.badge}>
           
            Our Solution
          </div>
          
          <h2 className={styles.title}>
            Faster, Smarter, Fairer
            <br />
            <span className={styles.titleGradient}>Credit Intelligence</span>
          </h2>
          
          <p className={styles.subtitle}>
            Transform credit decisions from slow, subjective processes into fast, data-driven intelligence with our unified AI platform
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div 
          className={styles.tabNavigation}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {solutions.map((solution, index) => (
            <button
              key={solution.id}
              className={`${styles.tab} ${activeTab === index ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(index)}
            >
              <div className={styles.tabTag}>{solution.tag}</div>
              <div className={styles.tabTitle}>{solution.title}</div>
              {activeTab === index && (
                <motion.div 
                  className={styles.tabIndicator}
                  layoutId="activeTab"
                  transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
                />
              )}
            </button>
          ))}
        </motion.div>

        {/* Content Area */}
        <motion.div 
          className={styles.contentArea}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <div className={styles.contentGrid}>
            
            {/* Left Side - Info */}
            <motion.div 
              className={styles.infoSection}
              key={activeTab}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className={styles.infoHeader}>
                <div className={`${styles.infoTag} ${styles[`tag${activeContent.color}`]}`}>
                  {activeContent.tag}
                </div>
                <h3 className={styles.infoTitle}>{activeContent.title}</h3>
                <p className={styles.infoSubtitle}>{activeContent.subtitle}</p>
              </div>

              <p className={styles.infoDescription}>{activeContent.description}</p>

              <div className={styles.featuresList}>
                {activeContent.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className={styles.featureItem}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className={styles.featureIcon}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="9" stroke="url(#featureGradient)" strokeWidth="2"/>
                        <path d="M6 10L9 13L14 7" stroke="url(#featureGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <defs>
                          <linearGradient id="featureGradient" x1="2" y1="2" x2="18" y2="18">
                            <stop stopColor="#00D9FF"/>
                            <stop offset="1" stopColor="#E91E63"/>
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>

              
            </motion.div>

            {/* Right Side - Screenshot */}
            <motion.div 
              className={styles.screenshotSection}
              key={`screenshot-${activeTab}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
            >
              <div className={styles.screenshotWrapper}>
                <div className={styles.screenshotGlow}></div>
                <div className={styles.screenshotFrame}>
                  <div className={styles.browserBar}>
                    <div className={styles.browserDots}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className={styles.browserUrl}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M5 7H9M7 5V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      scorex.io/{activeContent.id}
                    </div>
                  </div>
                  <div className={styles.screenshotImage}>
                    <img src={activeContent.screenshot} alt={activeContent.title} />
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.div>

        {/* Bottom Stats */}
        <motion.div 
          className={styles.statsRow}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          <div className={styles.statBox}>
            <div className={styles.statValue}>7min</div>
            <div className={styles.statLabel}>Approval Time</div>
          </div>
          <div className={styles.statBox}>
            <div className={styles.statValue}>92%</div>
            <div className={styles.statLabel}>Accuracy</div>
          </div>
          <div className={styles.statBox}>
            <div className={styles.statValue}>24/7</div>
            <div className={styles.statLabel}>Availability</div>
          </div>
          <div className={styles.statBox}>
            <div className={styles.statValue}>150+</div>
            <div className={styles.statLabel}>Data Points</div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Solution;